<template>
    <app-layout v-if="isPageReady">
        <common-content-spacer height="medium">
            <common-content-spacer height="small">
                <common-breadcrumbs v-if="!showConfirm" :breadcrumbs="pageBreadcrumbs"></common-breadcrumbs>

                <div class="heading-row">
                    <common-page-header v-if="!showConfirm" :heading="resultsHeaderHeading"></common-page-header>
                    <button
                        v-if="!isReferralFormShown && !showConfirm"
                        class="view-map-btn"
                        :class="{ 'view-map-btn--active': showMap }"
                        @click="showMap = !showMap"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <path
                                d="M14.106 5.553a2 2 0 0 0 1.788 0l3.659-1.83A1 1 0 0 1 21 4.619v12.764a1 1 0 0 1-.553.894l-4.553 2.277a2 2 0 0 1-1.788 0l-4.212-2.106a2 2 0 0 0-1.788 0l-3.659 1.83A1 1 0 0 1 3 19.381V6.618a1 1 0 0 1 .553-.894l4.553-2.277a2 2 0 0 1 1.788 0z"
                            />
                            <path d="M15 5.764v15" />
                            <path d="M9 3.236v15" />
                        </svg>
                        {{ showMap ? 'Close map' : 'View on map' }}
                    </button>
                </div>
            </common-content-spacer>

            <div v-if="!isReferralFormShown && !showConfirm" ref="topToolbar" class="selection-toolbar">
                <button
                    class="selection-toolbar__btn"
                    :class="{ 'selection-toolbar__btn--disabled': selectedCount === 0 }"
                    :disabled="selectedCount === 0"
                    @click="toggleAllSelection"
                >
                    <span class="desktop-text">Clear all and custom selection ({{ selectedCount }} selected)</span>
                    <span class="mobile-text">Clear all & custom select({{ selectedCount }})</span>
                </button>
                <div class="selection-toolbar__actions">
                    <common-button
                        v-if="selectedCount"
                        :label="$t(`pages.referrals-pool.index.create.referrals_pool`)"
                        size="small"
                        type="primary"
                        @click="toggleReferralCreationForm"
                    ></common-button>
                    <div class="search-network-grid">
                        <div
                            v-for="groupTab in groupTabs"
                            :key="groupTab.name"
                            :class="groupTab.iconClass"
                            @click="viewType = groupTab.name"
                        >
                            <common-icon :name="groupTab.icon" size="large"></common-icon>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="!isReferralFormShown && !showConfirm" class="results" :class="{ 'results-map': showMap }">
                <common-content-spacer class="results-container" height="medium">
                    <common-loader
                        v-if="isProcessing && !results.length && !physicianRecords.length"
                        :isProcessing="true"
                    ></common-loader>
                    <template v-else>
                        <common-content-spacer
                            v-if="results.length || physicianRecords.length"
                            height="medium"
                            class="results-container--grid"
                            :class="{ 'results-container--grid__active': selectedPracticeId }"
                        >
                            <common-grid v-if="!isAncillaryMatch" :class="{ 'grid--list-view': viewType === 'list' }">
                                <common-grid-column
                                    v-for="(result, index) in results"
                                    :id="result?.practice?.id"
                                    :key="index"
                                    :class="{
                                        'results-container--grid__active--practice': selectedPracticeId == result?.id,
                                    }"
                                >
                                    <practice-member-card
                                        :id="result.id"
                                        :physician="result"
                                        :checkedValues="checkedValues"
                                        :showCardAvailability="false"
                                        :isSelectionAllowed="true"
                                        :viewType="viewType"
                                        :showEmailButton="false"
                                        :showPhoneInline="true"
                                        :showPhoneButton="false"
                                        :disableReferButton="checkedValues.length !== 1 || !checkedValues.includes(result.id)"
                                        :useExternalReferHandler="true"
                                        @refer="handleReferFromCard(result)"
                                        @checkAvailability="checkAvailability($event)"
                                        @selection-checked="selectionChecked"
                                        @selection-changed="selectionChanged"
                                    ></practice-member-card>
                                </common-grid-column>

                                <common-grid-column
                                    v-if="showPhysicianRecordsTitle"
                                    :key="results.length"
                                    class="results--physician-records-title"
                                >
                                    {{ $t('pages.referrals.new.steps.results.physicians_not_in_medmatch') }}
                                </common-grid-column>

                                <common-grid-column
                                    v-for="(result, index) in physicianRecords"
                                    :id="result?.practice?.id"
                                    :key="results.length + index + 1"
                                >
                                    <practice-member-card
                                        :physician="{ isRecord: true, ...result }"
                                        :isUserProfileLinkEnabled="false"
                                        :showNetworkInclusionStatus="false"
                                        :showCardAvailability="false"
                                        :isSelectionAllowed="true"
                                        :checkedValues="checkedValues"
                                        :viewType="viewType"
                                        :showEmailButton="false"
                                        :showPhoneInline="true"
                                        :showPhoneButton="false"
                                        :disableReferButton="checkedValues.length !== 1 || !checkedValues.includes(result.id)"
                                        :useExternalReferHandler="true"
                                        @refer="handleReferFromCard(result)"
                                        @selection-checked="selectionChecked"
                                        @selection-changed="selectionChanged"
                                    ></practice-member-card>
                                </common-grid-column>
                            </common-grid>

                            <common-grid v-if="isAncillaryMatch" :class="{ 'grid--list-view': viewType === 'list' }">
                                <common-grid-column
                                    v-for="(result, index) in results"
                                    :id="result?.id"
                                    :key="index"
                                    :class="{
                                        'results-container--grid__active--practice': selectedPracticeId == result?.id,
                                    }"
                                >
                                    <ancillary-member-card
                                        :ancillary="result"
                                        :checkedValues="checkedValues"
                                        :showCardAvailability="false"
                                        :isSelectionAllowed="true"
                                        :viewType="viewType"
                                        @selection-checked="selectionChecked"
                                        @selection-changed="selectionChanged"
                                    ></ancillary-member-card>
                                </common-grid-column>
                            </common-grid>
                        </common-content-spacer>
                        <common-buttons v-if="results.length || physicianRecords.length">
                            <common-button
                                v-if="showMoreButton"
                                :label="$t('pages.referrals.index.buttons.show_more')"
                                :isProcessing="isProcessing"
                                type="primary"
                                size="large"
                                @click="showMore()"
                            ></common-button>
                        </common-buttons>

                        <common-blank-slate v-else>
                            {{ $t('pages.referrals.new.steps.results.blank_slate.message') }}
                            <router-link
                                :to="{ name: 'pool.new.search', query: { practiceType: `${$route.query.practiceType}` } }"
                            >
                                {{ $t('pages.referrals.new.steps.results.blank_slate.link') }}
                            </router-link>
                        </common-blank-slate>
                    </template>
                </common-content-spacer>
                <div v-if="showMap" class="map-container">
                    <map-component
                        :markers="markers"
                        @popup-open="onPopupOpen"
                        @popup-close="selectedPracticeId = null"
                        @distance-changed="distanceChanged"
                    ></map-component>
                </div>
            </div>

            <div v-if="selectedCount && !isReferralFormShown && !showConfirm && !isTopToolbarVisible" class="results__sticky">
                <common-buttons class="results__sticky-buttons">
                    <common-button
                        :label="$t(`pages.referrals-pool.index.create.referrals_pool`)"
                        size="large"
                        type="primary"
                        @click="toggleReferralCreationForm"
                    ></common-button>
                </common-buttons>
            </div>

            <common-content-wrapper v-if="isReferralFormShown" width="large">
                <referrals-new-make-form
                    :checkedValues="checkedValues"
                    :submitLabel="'forms.referrals_pool.make.buttons.send'"
                    @startOver="toggleReferralCreationForm"
                    @sent="handleReferralsPoolSubmition"
                ></referrals-new-make-form>
            </common-content-wrapper>
            <referrals-confirmation
                v-if="showConfirm"
                :heading="$t('pages.referrals.new.steps.confirmation.heading')"
                type="success"
            >
                <template #content>
                    <common-summary>
                        <common-summary-item :label="$t('pages.referrals.new.steps.confirmation.summary.patient_name')">
                            <common-user-name :user="referralInfo?.patient"></common-user-name>
                        </common-summary-item>

                        <common-summary-item :label="$t('pages.referrals.new.steps.confirmation.summary.diagnosis')">
                            {{ referralInfo.diagnosis }}
                        </common-summary-item>
                    </common-summary>
                </template>

                <template #footer>
                    <common-buttons>
                        <common-button
                            :label="$t('pages.auth.signup.steps.congratulations.content.button.proceed_to_dashboard')"
                            size="large"
                            type="primary"
                            @click="$router.push({ name: 'index.home' })"
                        ></common-button>
                    </common-buttons>
                </template>
            </referrals-confirmation>
        </common-content-spacer>
    </app-layout>
</template>

<script>
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import PageWithElementsMixin from 'mixins/PageWithElementsMixin'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import ReferralsNewPageMixin from 'mixins/ReferralsNewPageMixin'
    import ProviderDatabaseAPI from 'api/ProviderDatabaseAPI'
    import FacilityServicesAPI from 'api/FacilityServicesAPI'
    import AppLayout from 'components/layouts/AppLayout'
    import PracticeMemberCard from 'components/page-elements/practice/PracticeMemberCard'
    import AncillaryMemberCard from 'components/page-elements/practice/AncillaryMemberCard'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import MapComponent from 'components/page-elements/map/MapComponent'
    import ReferralsNewMakeForm from 'components/forms/ReferralsNewMakeForm'
    import ReferralsPoolAPI from 'api/ReferralsPoolAPI'
    import FormMixin from 'mixins/FormMixin'
    import ReferralsConfirmation from 'components/page-elements/ReferralsConfirmation'
    import PracticePlanEnum from '@/enums/PracticePlanEnum'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'

    export default {
        mixins: [
            PageWithTitleMixin,
            PageWithElementsMixin,
            PageWithDataMixin,
            ReferralsNewPageMixin,
            FormMixin,
            ScreenSizeDetectionMixin,
        ],

        components: {
            AppLayout,
            PracticeMemberCard,
            AncillaryMemberCard,
            MapComponent,
            ReferralsNewMakeForm,
            ReferralsConfirmation,
        },

        data() {
            return {
                results: [],
                physicianRecords: [],
                isMapOpen: false,
                isProcessing: false,
                offset: 0,
                totalCount: 0,
                physicianRecordsTotalCount: 0,
                PracticeTypeEnum: PracticeTypeEnum,
                showMap: false,
                markers: [],
                selectedPracticeId: null,
                checkedValues: [],
                isReferralFormShown: false,
                showConfirm: false,
                referralInfo: null,
                isAllSelected: false,
                isAllSelectedFromYourNetwork: false,
                selectionType: null,
                deselectedIds: [],
                totalProvidersIds: null,
                providersWithinNetwork: null,
                isAncillaryMatch: undefined,
                viewType: 'grid',
                scrollPosition: 0,
                isTopToolbarVisible: true,
                isInitialResultsLoading: true,
            }
        },

        computed: {
            resultsHeaderHeading() {
                const noResultsYet = !this.results.length && !this.physicianRecords.length
                if (noResultsYet && (this.isInitialResultsLoading || this.isProcessing)) {
                    return this.$t('pages.referrals.new.steps.results.searching')
                }
                const networkPart = this.providersWithinNetwork?.userIds?.length ? ` ${this.pageHeadingNetwork}` : ''
                return `${this.pageHeading}${networkPart}`
            },

            pageHeading() {
                const practiceType = this.$route.query.practiceType

                return this.$tc(`pages.referrals.new.steps.results.heading.${practiceType}`, this.totalCount, {
                    count: this.totalCount,
                })
            },

            pageHeadingNetwork() {
                return this.$tc(
                    `pages.referrals.new.steps.results.heading.network`,
                    this.providersWithinNetwork?.userIds?.length,
                    {
                        count: this.providersWithinNetwork?.userIds?.length,
                    },
                )
            },

            groupTabs() {
                return [
                    { name: 'grid', icon: 'grid', class: 'toggle-icon-left' },
                    { name: 'list', icon: 'list', class: 'toggle-icon-right' },
                ].map((tab) => ({
                    ...tab,
                    iconClass: {
                        'toggle-view-icon': true,
                        [tab.class]: true,
                        active: this.viewType === tab.name,
                    },
                }))
            },

            showMoreButton() {
                return this.totalCount > this.results.length || this.physicianRecordsTotalCount > this.physicianRecords.length
            },

            showPhysicianRecordsTitle() {
                return this.physicianRecordsTotalCount >= this.physicianRecords.length && this.physicianRecordsTotalCount
            },

            selectedCount() {
                if (this.selectionType === 'ALL') {
                    const baseCount = this.totalCount || this.totalProvidersIds?.length || this.checkedValues.length
                    return Math.max(baseCount - this.deselectedIds.length, 0)
                }
                return this.checkedValues.length
            },
        },

        beforeRouteLeave(to, from, next) {
            if (this.isReferralFormShown) {
                const answer = window.confirm(this.$t('pages.referrals.new.steps.confirmation.leave'))
                if (answer) {
                    next()
                } else {
                    next(false)
                }
            } else {
                next()
            }
        },

        methods: {
            getValidatedProviderSearchLimit(limit) {
                const parsed = Number(limit) || 12
                return Math.min(Math.max(parsed, 1), 50)
            },

            normalizeProviderSearchResponse(response) {
                if (Array.isArray(response)) return response
                if (Array.isArray(response?.data)) return response.data
                return []
            },

            async getAllSelectedIdsForSubmit() {
                // CUSTOM selection should always submit exactly what user checked.
                if (this.selectionType !== 'ALL') {
                    return this.checkedValues
                }

                // Ancillary already returns full id set from API.
                if (this.$route.query.practiceType === PracticeTypeEnum.ANCILLARY) {
                    const allIds = this.totalProvidersIds?.length ? this.totalProvidersIds : this.checkedValues
                    return (allIds || []).filter((id) => !this.deselectedIds.includes(String(id)))
                }

                // Physician "ALL": fetch all pages to collect complete IDs/NPIs.
                const limit = this.getValidatedProviderSearchLimit(this.$route.query.limit)
                const total = Number(this.totalCount || 0)
                const pages = total > 0 ? Math.ceil(total / limit) : 1
                const allIds = []

                for (let pageIndex = 0; pageIndex < pages; pageIndex += 1) {
                    const offset = pageIndex * limit
                    const response = await ProviderDatabaseAPI.search({
                        zip: this.$route.query.zip,
                        city: this.$route.query.city,
                        state: this.$route.query.state,
                        specialityId: this.$route.query.specialityId,
                        limit,
                        offset,
                    })

                    const rawProviders = this.normalizeProviderSearchResponse(response)
                    rawProviders.forEach((provider) => {
                        const mapped = this.mapProviderToResult(provider)
                        if (mapped?.id != null) {
                            allIds.push(mapped.id)
                        }
                    })
                }

                return [...new Set(allIds.map((id) => String(id)))].filter((id) => !this.deselectedIds.includes(String(id)))
            },

            loadMatchesWithDifferentRange(range) {
                this.getMatches({ ...this.$route.query, range, offset: this.offset })
            },

            handleScroll() {
                this.scrollPosition = window.scrollY || document.documentElement.scrollTop
                if (this.$refs.topToolbar) {
                    this.isTopToolbarVisible = this.$refs.topToolbar.getBoundingClientRect().bottom > 0
                }
            },

            selectAllProviders() {
                this.checkedValues = this.results.map((item) => item.id)
                this.selectionType = 'ALL'
                if (this.deselectedIds.length) {
                    this.checkedValues = this.checkedValues.filter((id) => !this.deselectedIds.includes(String(id)))
                }
            },

            toggleAllSelection() {
                const getSubscription = (item) => (this.isAncillaryMatch ? item.subscription : item.practice.subscription)

                if (this.checkedValues.length > 0) {
                    this.checkedValues = []
                    this.selectionType = 'CUSTOM'
                    this.deselectedIds = []
                } else {
                    this.checkedValues = this.results
                        .filter((item) => getSubscription(item) !== PracticePlanEnum.NETWORK)
                        .map((item) => item.id)
                    this.selectionType = 'ALL'
                    this.deselectedIds = []
                }
            },

            checkAvailability(query) {
                const q = { ...this.$route.query, ...query }
                if (!q.subSpecialityId) {
                    const physicianSpecialities = query.target.specialities
                    q.subSpecialityId = physicianSpecialities.find((specObj) => {
                        return Number(specObj.speciality.id) === Number(q.specialityId)
                    }).subSpeciality.id
                }
                this.$router.push({
                    name: 'referrals.new.make',
                    query: q,
                    params: {
                        existingReferral: this.$route.params.existingReferral,
                        originalQuery: this.$route.query,
                    },
                })
            },

            toggleMapView(flag = !this.isMapOpen) {
                this.isMapOpen = flag
            },

            getMatches(data, append) {
                this.isProcessing = true
                if (this.results.length < this.totalCount || this.physicianRecords.length < this.physicianRecordsTotalCount) {
                    return this.$route.query.practiceType === PracticeTypeEnum.ANCILLARY
                        ? this.fetchAncillaryMatches({ ...this.$route.query, offset: this.offset }, append)
                        : this.fetchMatches({ ...this.$route.query, offset: this.offset }, append)
                }
                this.isProcessing = false
            },

            fetchMatches(data, append) {
                this.isProcessing = true
                ProviderDatabaseAPI.search({
                    zip: data.zip,
                    city: data.city,
                    state: data.state,
                    specialityId: data.specialityId,
                    limit: this.getValidatedProviderSearchLimit(data.limit),
                    offset: data.offset || 0,
                })
                    .then((response) => {
                        this.isProcessing = false

                        const rawProviders = Array.isArray(response)
                            ? response
                            : Array.isArray(response.data)
                            ? response.data
                            : []
                        const totalCount = parseInt(response._headers?.['x-total-count']) || rawProviders.length
                        const allProviders = rawProviders.map((provider) => this.mapProviderToResult(provider))

                        if (append) {
                            const shouldKeepAllSelected = this.selectionType === 'ALL' && this.checkedValues.length > 0
                            this.results = this.results.concat(allProviders)
                            this.getLocationsMarkers(this.results)
                            if (shouldKeepAllSelected) {
                                this.selectAllProviders()
                            }
                            return
                        }

                        this.totalProvidersIds = allProviders.map((p) => p.id)
                        this.providersWithinNetwork = { userIds: [], count: 0 }
                        this.results = allProviders
                        this.physicianRecords = []
                        this.getLocationsMarkers(this.results)
                        this.totalCount = totalCount
                        this.physicianRecordsTotalCount = 0
                        this.offset = 0
                        this.selectAllProviders()
                        this.isInitialResultsLoading = false
                    })
                    .catch(() => {
                        this.isProcessing = false
                        this.isInitialResultsLoading = false
                    })
            },

            mapProviderToResult(provider) {
                const practiceAddress =
                    provider.addresses?.find((addr) => addr.addressType === 'Practice') || provider.addresses?.[0] || {}
                const providerNpi = provider.npi || provider.npiNumber || provider.providerNpi || null

                const locations =
                    provider.addresses?.map((addr) => ({
                        id: addr.id,
                        address: addr.firstLineAddress,
                        addressLine2: addr.secondLineAddress,
                        city: addr.cityName,
                        state: addr.stateName,
                        zip: addr.postalCode,
                        phone: addr.telephoneNumber,
                        fax: addr.faxNumber,
                        latitude: null,
                        longitude: null,
                    })) || []

                const primaryTaxonomy =
                    provider.providerTaxonomies?.find((t) => t.healthcareProviderPrimaryTaxonomySwitch) ||
                    provider.providerTaxonomies?.[0]
                const specialities =
                    provider.providerTaxonomies?.map((tax) => ({
                        speciality: {
                            id: tax.taxonomy?.medmatch_speciality_id,
                            name: tax.taxonomy?.classification,
                        },
                        subSpeciality: {
                            id: tax.taxonomy?.medmatch_subspeciality_id,
                            name: tax.taxonomy?.specialization || tax.taxonomy?.displayName,
                        },
                    })) || []

                return {
                    id: providerNpi || provider.id || provider._id,
                    npi: providerNpi || provider.npi,
                    firstName: provider.firstName || provider.user?.firstName,
                    lastName: provider.lastName || provider.user?.lastName,
                    phone: practiceAddress.telephoneNumber,
                    credential: provider.user?.credential,
                    genderCode: provider.user?.genderCode,
                    specialization: provider.specialization,
                    city: provider.city,
                    state: provider.state,
                    specialities,
                    practice: {
                        id: providerNpi || provider.id || provider._id,
                        name: `${provider.firstName || provider.user?.firstName} ${provider.lastName || provider.user?.lastName}`,
                        subscription: null,
                        locations,
                        address: practiceAddress.firstLineAddress,
                        city: practiceAddress.cityName,
                        state: practiceAddress.stateName,
                        zip: practiceAddress.postalCode,
                        phone: practiceAddress.telephoneNumber,
                    },
                    taxonomy: primaryTaxonomy?.taxonomy,
                    _raw: provider,
                }
            },

            fetchAncillaryMatches(data, append) {
                this.isProcessing = true
                data.testIds = data.testIds.toString()
                if (this.$route.query.practiceType === PracticeTypeEnum.ANCILLARY) {
                    data.testIds = data.testIds.toString()
                }
                FacilityServicesAPI.getMatchesList(data)
                    .then((results) => {
                        this.isProcessing = false
                        if (append) {
                            const shouldKeepAllSelected = this.selectionType === 'ALL' && this.checkedValues.length > 0
                            this.results = this.results.concat(results.providers)
                            this.getAncillaryLocationsMarkers(this.results)
                            if (shouldKeepAllSelected) {
                                this.selectAllProviders()
                            }
                            return
                        }
                        this.results = results.providers
                        this.totalProvidersIds = results.totalProvidersIds
                        this.getAncillaryLocationsMarkers(this.results)
                        this.totalCount = Number(results._headers['x-total-count'])
                        this.selectAllProviders()

                        if (this.results?.length === this.totalCount) {
                            this.offset = 0
                        }
                        this.isInitialResultsLoading = false
                    })
                    .catch(() => {
                        this.isProcessing = false
                        this.isInitialResultsLoading = false
                    })
            },

            distanceChanged(distance) {
                this.$route.query.practiceType === PracticeTypeEnum.ANCILLARY
                    ? this.fetchAncillaryMatches({ ...this.$route.query, distance, offset: this.offset }, false)
                    : this.fetchMatches({ ...this.$route.query, distance, offset: this.offset }, false)
            },

            showMore() {
                this.offset = Number(this.$route.query.limit) + this.offset
                this.getMatches({ ...this.$route.query, offset: this.offset }, true)
            },

            getLocationsMarkers(results) {
                const locations = []
                this.markers = []
                results.forEach((item) => {
                    const practiceLocations = item.practice?.locations || []
                    practiceLocations.forEach((location) => {
                        const locationWithPracticeName = { ...location, item }
                        if (location.latitude && location.longitude) {
                            locations.push(locationWithPracticeName)
                        } else if (location.address || location.city) {
                            locations.push(locationWithPracticeName)
                        }
                    })
                })
                this.markers = locations
            },

            getAncillaryLocationsMarkers(results) {
                const locations = []
                this.markers = []
                results.forEach((item) => {
                    item.locations.forEach((location) => {
                        const locationWithPracticeName = { ...location, item }
                        locationWithPracticeName.longitude = location?.coordinates?.longitude
                        locationWithPracticeName.latitude = location?.coordinates?.latitude
                        locations.push(locationWithPracticeName)
                    })
                })
                this.markers = locations
            },

            onPopupOpen(event) {
                this.selectedPracticeId = event
                const el = document.getElementById(this.selectedPracticeId)
                el.scrollIntoView({
                    behavior: 'auto',
                    block: 'center',
                    inline: 'center',
                })
            },

            selectionChecked(id) {
                this.isChecked = this.checkedValues.includes(id)
            },

            selectionChanged(id) {
                if (this.selectionType === 'ALL') {
                    const deselectedId = String(id)
                    if (this.checkedValues.includes(id)) {
                        this.checkedValues.splice(this.checkedValues.indexOf(id), 1)
                        if (!this.deselectedIds.includes(deselectedId)) {
                            this.deselectedIds.push(deselectedId)
                        }
                    } else {
                        this.checkedValues.push(id)
                        this.deselectedIds = this.deselectedIds.filter((item) => item !== deselectedId)
                    }
                    return
                }
                if (this.checkedValues.includes(id)) {
                    this.checkedValues.splice(this.checkedValues.indexOf(id), 1)
                } else {
                    this.checkedValues.push(id)
                }
            },

            toggleReferralCreationForm() {
                this.isReferralFormShown = !this.isReferralFormShown
            },

            handleReferFromCard(result) {
                const id = result?.id
                if (id && !this.checkedValues.includes(id)) {
                    this.checkedValues = [id]
                }
                if (!this.isReferralFormShown) {
                    this.toggleReferralCreationForm()
                }
            },

            buildCreateWithFeasibilityPayload(selectedIds = this.checkedValues || []) {
                const allResults = [...this.results, ...this.physicianRecords]
                const providerNpis = []
                const userIds = []
                const isFromMedMatchResults = (id) => this.results.some((r) => String(r.id) === String(id))

                ;(selectedIds || []).forEach((id) => {
                    const idStr = String(id)
                    const result = allResults.find((r) => String(r.id) === idStr)
                    const npi = result?.npi != null ? String(result.npi) : idStr
                    const isNpi = /^\d{10}$/.test(npi)
                    if (isNpi) {
                        providerNpis.push(npi)
                    } else if (result && isFromMedMatchResults(id)) {
                        userIds.push(Number(id))
                    }
                })

                return {
                    providerNpis: [...new Set(providerNpis)],
                    ...(userIds.length ? { userIds: [...new Set(userIds)] } : {}),
                }
            },

            async handleReferralsPoolSubmition(event) {
                delete event.fromUserId
                delete event.toUserId
                this.referralInfo = event

                try {
                    const selectedIdsForSubmit = await this.getAllSelectedIdsForSubmit()
                    const feasibilityPayload = this.buildCreateWithFeasibilityPayload(selectedIdsForSubmit)
                    const data =
                        this.$route.query.practiceType === PracticeTypeEnum.ANCILLARY
                            ? { practiceIds: selectedIdsForSubmit, ...event }
                            : {
                                  ...event,
                                  ...feasibilityPayload,
                              }
                    data.searchBy = this.$route.query.searchBy
                    data.searchByZip = this.$route.query.zip
                    data.searchByAddress = this.$route.query.address
                    data.searchByCity = this.$route.query.city
                    data.searchByState = this.$route.query.state
                    data.timeframe = this.$route.query.searchByTimeWindow
                    data.fromUserId = this.$auth.getActiveMember().id

                    if (this.$route.query.testIds) {
                        data.referralTests = this.$route.query.testIds
                            .toString()
                            .split(',')
                            .map((item) => Number(item))
                    }
                    await this.handleRequest(ReferralsPoolAPI.create(data, { feasibility: true }))
                    this.toggleShowConfirm()
                    this.toggleReferralCreationForm()
                } catch {
                    return
                }
            },

            toggleShowConfirm() {
                this.showConfirm = !this.showConfirm
            },

            selectAllToggle() {
                this.isAllSelected = !this.isAllSelected
                if (this.isAllSelected) {
                    this.checkedValues = this.results.map((record) => record.id)
                } else {
                    this.checkedValues = []
                }
            },

            selectAllFromYourNetworkToggle() {
                this.isAllSelectedFromYourNetwork = !this.isAllSelectedFromYourNetwork

                if (this.isAllSelectedFromYourNetwork) {
                    this.checkedValues = this.results
                        .filter((record) => record.physicianGroup?.id === 4)
                        .map((record) => record.id)
                } else {
                    this.checkedValues = []
                }
            },
        },

        mounted() {
            window.addEventListener('scroll', this.handleScroll)
            this.isAncillaryMatch = this.$route.query.practiceType === PracticeTypeEnum.ANCILLARY
            this.isAncillaryMatch
                ? this.fetchAncillaryMatches({ ...this.$route.query, offset: this.offset })
                : this.fetchMatches({ ...this.$route.query, offset: this.offset })
        },

        beforeDestroy() {
            window.removeEventListener('scroll', this.handleScroll)
        },
    }
</script>

<style lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';

    .heading-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;

        @media (max-width: 767px) {
            flex-wrap: wrap;
            gap: 8px;
        }
    }

    .view-map-btn {
        display: flex;
        align-items: center;
        gap: 6px;
        background: none;
        border: none;
        cursor: pointer;
        font-size: 14px;
        font-weight: 400;
        color: #00bcd4;
        padding: 4px 0;
        margin-left: auto;
        white-space: nowrap;
        transition: color 0.2s ease;

        &:hover {
            color: #00bcd4;
            text-decoration: underline;
        }

        &--active {
            color: #00bcd4;
        }
    }

    .search-network-grid {
        display: flex;
        gap: 0.25rem;
        background-color: oklch(96.7% 0.003 264.542);
        border-radius: 0.5rem;
        padding: 0.25rem;
        align-items: center;

        @media (max-width: 767px) {
            display: none;
        }
    }

    .toggle-view-icon {
        padding: 4px;
        cursor: pointer;
        min-width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 0.375rem;
        background-color: transparent;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        color: #9ca3af;

        &:hover {
            color: #6b7280;
            background-color: #fff;
        }

        &.active {
            background-color: #fff;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
            color: #6b7280;
        }
    }

    .grid--list-view {
        .grid__column {
            flex-basis: 100% !important;
            max-width: 100% !important;
        }
    }

    .results {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
        @include breakpoint(laptop) {
            flex-direction: column-reverse;
            padding-bottom: 50px;
        }
        .map-container {
            height: 600px;
        }

        .results-container {
            display: flex;
            flex-wrap: wrap;
            width: 100%;

            &--grid {
                width: 100%;
                overflow: hidden;
                padding: 10px;

                &__active {
                    .card {
                        opacity: 0.5;
                    }

                    &--practice {
                        .card {
                            opacity: 1;
                        }
                    }
                }
            }

            .buttons {
                width: 100%;
            }
        }

        &__sticky {
            @include breakpoint(tablet) {
                width: 100%;
                height: 120px;
            }
            position: fixed;
            width: 100%;
            height: 60px;
            bottom: 0;
            right: 0;
            background-color: white;

            &-buttons {
                @include breakpoint(tablet) {
                    margin-left: 0;
                    margin-top: 30px;
                }
                margin: 5px;
                margin-left: 220px;
            }
        }
    }

    .selection-toolbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 16px;
        padding: 8px 0;

        @media (max-width: 767px) {
            flex-direction: row;
            align-items: stretch;
            justify-content: space-between;
            gap: 8px;
        }

        &__actions {
            display: flex;
            align-items: center;
            gap: 16px;

            @media (max-width: 767px) {
                flex: 1;
                align-items: stretch;
                justify-content: flex-end;
                gap: 8px;

                ::v-deep .button,
                ::v-deep button {
                    height: 100%;
                    min-height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
            }
        }

        &__btn {
            background: #13b3e3;
            color: #fff;
            border: none;
            border-radius: 10px;
            padding: 10px 24px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.3s ease;
            white-space: nowrap;

            .desktop-text {
                display: inline;
            }
            .mobile-text {
                display: none;
            }

            @media (max-width: 767px) {
                text-align: center;
                padding: 8px 12px;
                font-size: 13px;
                flex: 1;
                line-height: 1.2;
                display: flex;
                align-items: center;
                justify-content: center;

                .desktop-text {
                    display: none;
                }
                .mobile-text {
                    display: inline;
                }
            }

            &:hover {
                background: darken(#13b3e3, 5%);
            }

            &--disabled {
                background: #f3f4f6;
                color: #9ca3af;
                cursor: default;

                &:hover {
                    background: #f3f4f6;
                }
            }
        }
    }

    .results-map {
        .map-container {
            @include breakpoint(laptop) {
                width: 100%;
                max-height: 600px;
                height: 600px;
                margin-bottom: 70px;
            }

            .map-component {
                @include breakpoint(laptop) {
                    width: 100%;
                    max-height: 600px;
                    height: 600px;
                }
            }
            width: 450px;
        }

        .results-container {
            .grid__column {
                @include breakpoint(tablet) {
                    flex-basis: 100%;
                }
                flex-basis: calc(100% / 2);
            }
            @include breakpoint(laptop) {
                width: 100%;
            }
            width: calc(100% - 550px);
        }
    }
</style>
